import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSelector } from "react-redux";
import AddCustomFieldsModal from "./AddCustomFieldsModal";

const MAX_CUSTOM_FIELDS = 10;

const compactAlertSx = {
  py: "6px",
  alignItems: "center",
  "& .MuiAlert-message": { py: "4px" },
  "& .MuiAlert-icon": { py: "2px", alignItems: "center" },
};

const infoAlertSx = {
  ...compactAlertSx,
  bgcolor: "grey.100",
  color: "text.primary",
  "& .MuiAlert-icon": {
    ...compactAlertSx["& .MuiAlert-icon"],
    color: "text.secondary",
  },
};

const tableCellSx = {
  py: "12px",
};

const CustomFieldsSettingsSection = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const customFieldsList = useSelector(
    (state) => state.org.org.settings?.custom_fields ?? [],
  );

  const fieldCount = customFieldsList.length;
  const isAtLimit = fieldCount >= MAX_CUSTOM_FIELDS;

  const existingFieldNames = useMemo(
    () => customFieldsList.map((field) => field.field_name).filter(Boolean),
    [customFieldsList],
  );

  return (
    <>
      <Paper elevation={0} sx={{ pl: "1rem", pb: "1rem" }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ mt: "2rem", mb: "0.5rem" }}
        >
          Custom Field Settings
        </Typography>

        <Stack spacing={2}>
          <Stack spacing={1}>
            <Alert severity="warning" sx={compactAlertSx}>
              Field Names (snake_case) cannot be edited once saved.
            </Alert>
            <Alert severity="info" sx={infoAlertSx}>
              Maximum of 10 total custom fields per organization. Field values
              are restricted to a maximum length of 256 characters in API
              invoice payloads.
            </Alert>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography variant="subtitle1">
                Custom Metadata Fields
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fieldCount} / 10 Fields Configured
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={isAtLimit}
              onClick={() => setIsModalOpen(true)}
            >
              Add Custom Field
            </Button>
          </Stack>

          <TableContainer component={Paper} variant="outlined" elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableCellSx}>FIELD NAME</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fieldCount ? (
                  customFieldsList.map((field) => (
                    <TableRow key={field.field_name}>
                      <TableCell sx={tableCellSx}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LockOutlinedIcon
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: "monospace",
                              fontSize: "0.875rem",
                            }}
                          >
                            {field.field_name}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" sx={tableCellSx}>
                      No custom metadata fields configured yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>

      <AddCustomFieldsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        existingFieldNames={existingFieldNames}
      />
    </>
  );
};

export default CustomFieldsSettingsSection;
